/**
 * embed.js entry point — Sprint 3 / updated Sprint 5
 *
 * Scans the DOM for [data-mp-org] elements and mounts the appropriate mode.
 * Also installs the window.ManagePatients API.
 *
 * Analytics session ID:
 *   - Generated once per page load via crypto.randomUUID()
 *   - Stored in sessionStorage under 'mp_session_id'
 *   - Passed to every iframe as ?sessionId= so the iframe can include it in
 *     postMessage payloads and the host can correlate events
 *   - NOT generated (null) when navigator.doNotTrack === '1'
 *
 * Auto-init fires on DOMContentLoaded (or immediately if the DOM is already
 * ready). Re-init is a no-op — safe to call multiple times.
 *
 * Supported modes:
 *   data-mp-mode="inline"   — inject iframe inline in the page
 *   data-mp-mode="popup"    — open iframe in a modal when the trigger is clicked
 *   data-mp-mode="floating" — FAB button in the viewport corner (default: bottom-right)
 *
 * Example markup:
 *   <!-- Popup button -->
 *   <button data-mp-org="ukearcare" data-mp-mode="popup">Book now</button>
 *
 *   <!-- Inline widget -->
 *   <div data-mp-org="ukearcare" data-mp-mode="inline"></div>
 *
 *   <!-- Floating button -->
 *   <div data-mp-org="ukearcare" data-mp-mode="floating" data-mp-color="#0ea5e9"></div>
 */

import { parseConfig } from './config'
import { mountInline }   from './modes/inline'
import { mountPopup }    from './modes/popup'
import { mountFloating } from './modes/floating'
import { installAPI }    from './api'

const SELECTOR = '[data-mp-org]'
let initialised = false

/**
 * Generate or retrieve the analytics session ID.
 * Returns null if the visitor has DNT enabled or if the browser lacks
 * crypto.randomUUID (very old, pre-2021 browsers).
 */
function getSessionId(): string | null {
  if (typeof navigator !== 'undefined' && navigator.doNotTrack === '1') return null

  try {
    const stored = sessionStorage.getItem('mp_session_id')
    if (stored) return stored

    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      const id = crypto.randomUUID()
      sessionStorage.setItem('mp_session_id', id)
      return id
    }
  } catch {
    // sessionStorage blocked (e.g. private browsing with strict settings)
  }

  return null
}

function init(): void {
  if (initialised) return
  initialised = true

  // Install window.ManagePatients before processing triggers so that any
  // 'ready' listeners queued before the script loaded are replayed first.
  installAPI()

  const sessionId = getSessionId()

  const triggers = Array.from(document.querySelectorAll<HTMLElement>(SELECTOR))

  for (const trigger of triggers) {
    const config = parseConfig(trigger)
    if (!config) continue

    // Attach session ID so the iframe can include it in postMessage payloads
    config.sessionId = sessionId

    switch (config.mode) {
      case 'inline':
        mountInline(trigger, config)
        break
      case 'popup':
        mountPopup(trigger, config)
        break
      case 'floating':
        mountFloating(trigger, config)
        break
    }
  }
}

// Auto-init as soon as the DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  // DOMContentLoaded already fired (script loaded with defer/async and DOM is ready)
  init()
}
